import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react-native';
import { useState, type ReactNode } from 'react';
import { Pressable } from 'react-native';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * A titled section that opens in place: one row with a caret, and its content below once opened.
 * Closed by default, so a long list does not push the rest of a screen out of view.
 *
 * The caret points down while closed and up while open, as the inbox's inquiry group cards do. A
 * right-pointing chevron is kept for rows that open another screen, so the two never read alike.
 * The content grows the page rather than scrolling inside it, because a scroll area nested in a
 * scrolling screen fights the page for the drag on a phone.
 */
export function DisclosureSection({
  title,
  accessibilityLabel,
  children,
}: {
  title: string;
  /** What a screen reader announces, when the visible title is a shorthand such as "23/33". */
  accessibilityLabel?: string;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <VStack space="sm">
      <Pressable
        onPress={() => setIsOpen((open) => !open)}
        accessibilityState={{ expanded: isOpen }}
        {...getAccessibilityProps({ role: 'button', label: accessibilityLabel ?? title })}>
        <HStack className="min-h-[48px] items-center justify-between">
          <Text className="font-semibold text-typography-950">{title}</Text>

          <Icon as={isOpen ? ChevronUpIcon : ChevronDownIcon} className="text-typography-700" />
        </HStack>
      </Pressable>

      {isOpen ? <VStack space="sm">{children}</VStack> : null}
    </VStack>
  );
}
